"use client";

import { useState, useRef, useEffect, FormEvent } from "react";
import Link from "next/link";
import { MessageCircle, X, Send, Download, Mail } from "lucide-react";
import ReactMarkdown from "react-markdown";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

type ChatResponse = {
  reply?: string;
  error?: string;
};

export function ExpertChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [hasConsentedToChat, setHasConsentedToChat] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const contactHref = "/#kontakt";

  // Ladda historik från sessionStorage vid mount
  useEffect(() => {
    const saved = sessionStorage.getItem("chat-history");
    if (saved) {
      try {
        setMessages(JSON.parse(saved));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  // Spara historik till sessionStorage
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem("chat-history", JSON.stringify(messages));
    }
  }, [messages]);

  // Auto-scroll till botten
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, messages]);

  // Lyssna på custom event för att öppna chatten med ett meddelande
  useEffect(() => {
    const handleOpenChatWithMessage = (event: CustomEvent<string>) => {
      const message = event.detail;
      if (!message.trim()) return;
      
      setIsOpen(true);
      setHasConsentedToChat(true); // Öppnad via CTA = användaren vill använda chatten

      // Vänta lite för att säkerställa att chatten är öppen, sedan skicka meddelandet
      setTimeout(() => {
        const trimmed = message.trim();
        if (!trimmed) return;
        
        // Hämta aktuell historik
        setMessages((prev) => {
          const currentHistory = prev;
          
          // Lägg till användarens meddelande
          const userMessage: ChatMessage = { role: "user", content: trimmed };
          const newMessages = [...prev, userMessage];
          
          setIsLoading(true);
          
          // Skicka meddelandet
          fetch("/api/chat", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              message: trimmed,
              history: currentHistory
            })
          })
            .then((res) => {
              if (!res.ok) {
                throw new Error("Kunde inte kontakta bilrådgivaren.");
              }
              return res.json();
            })
            .then((data: ChatResponse) => {
              if (data.reply) {
                setMessages([...newMessages, { role: "assistant", content: data.reply }]);
              } else {
                setMessages([
                  ...newMessages,
                  {
                    role: "assistant",
                    content: data.error || "Något gick fel, försök igen."
                  }
                ]);
              }
            })
            .catch((error) => {
              const errMsg =
                error instanceof Error
                  ? error.message
                  : "Kunde inte ansluta. Försök igen.";
              setMessages([
                ...newMessages,
                {
                  role: "assistant",
                  content: errMsg
                }
              ]);
            })
            .finally(() => {
              setIsLoading(false);
            });
          
          return newMessages;
        });
      }, 200);
    };

    window.addEventListener("openChatWithMessage", handleOpenChatWithMessage as EventListener);
    return () => {
      window.removeEventListener("openChatWithMessage", handleOpenChatWithMessage as EventListener);
    };
  }, []);

  const WELCOME_MESSAGE: ChatMessage = {
    role: "assistant",
    content:
      "Hej! Hur kan jag hjälpa dig en dag som denna? Vad är du ute efter, en ny bil, service, eller något annat? Skriv gärna här så guidar jag dig.",
  };

  const startNewChat = () => {
    setMessages([WELCOME_MESSAGE]);
    sessionStorage.setItem("chat-history", JSON.stringify([WELCOME_MESSAGE]));
  };

  const downloadChat = () => {
    if (messages.length === 0) return;

    const chatText = messages
      .map((msg) => {
        const role = msg.role === "user" ? "Du" : "Assistent";
        return `${role}: ${msg.content}`;
      })
      .join("\n\n");

    const blob = new Blob([chatText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bilspektrum-chatt-${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const emailChat = () => {
    if (messages.length === 0) return;

    const chatText = messages
      .map((msg) => {
        const role = msg.role === "user" ? "Kund" : "Assistent";
        return `${role}: ${msg.content}`;
      })
      .join("\n\n");

    const subject = encodeURIComponent("Chatt från Bilspektrum hemsida");
    const body = encodeURIComponent(
      `Hej!\n\nJag har haft följande konversation med chatten på Bilspektrums hemsida:\n\n${chatText}\n\nVänligen kontakta mig för att fortsätta diskussionen.\n\nMed vänliga hälsningar`
    );

    window.location.href = `mailto:info@bilspektrum.se?subject=${subject}&body=${body}`;
  };

  const handleContactRequest = async () => {
    if (!contactEmail || !contactEmail.includes("@")) {
      alert("Vänligen ange en giltig e-postadress");
      return;
    }

    setIsSubmittingContact(true);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: contactEmail,
          chatHistory: messages,
          message: contactMessage
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Öppna e-postklienten
        window.location.href = data.mailto;
        
        // Visa bekräftelse
        setMessages([
          ...messages,
          {
            role: "assistant",
            content: "Tack! Din kontaktförfrågan har skickats. Någon från Bilspektrum kommer att kontakta dig snart på " + contactEmail + "."
          }
        ]);

        // Rensa formuläret
        setContactEmail("");
        setContactMessage("");
        setShowContactForm(false);
      } else {
        throw new Error(data.error || "Kunde inte skicka kontaktförfrågan");
      }
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Kunde inte skicka kontaktförfrågan. Försök igen."
      );
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage = trimmed;
    setInput("");

    // Lägg till användarens meddelande
    const newMessages: ChatMessage[] = [
      ...messages,
      { role: "user", content: userMessage }
    ];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          message: userMessage,
          history: messages // Skicka tidigare historik
        })
      });

      if (!res.ok) {
        throw new Error("Kunde inte kontakta bilrådgivaren.");
      }

      const data = (await res.json()) as ChatResponse;

      if (data.reply) {
        setMessages([...newMessages, { role: "assistant", content: data.reply }]);
      } else {
        setMessages([
          ...newMessages,
          {
            role: "assistant",
            content: data.error || "Något gick fel, försök igen."
          }
        ]);
      }
    } catch (error) {
      const errMsg =
        error instanceof Error
          ? error.message
          : "Kunde inte ansluta. Försök igen.";
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: errMsg
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as FormEvent<HTMLFormElement>);
    }
  };

  // Konvertera vanliga URL:er till markdown-länkar
  const convertUrlsToLinks = (text: string): string => {
    // Först, skydda redan existerande markdown-länkar
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const placeholders: string[] = [];
    let placeholderIndex = 0;
    
    // Ersätt markdown-länkar med placeholders
    let protectedText = text.replace(markdownLinkRegex, (match) => {
      placeholders.push(match);
      return `__MARKDOWN_LINK_${placeholderIndex++}__`;
    });
    
    // Hitta och konvertera vanliga URL:er
    const urlRegex = /(https?:\/\/[^\s\)]+)/g;
    protectedText = protectedText.replace(urlRegex, (url) => {
      // Ta bort eventuella avslutande tecken som inte ska ingå i länken
      const cleanUrl = url.replace(/[.,;:!?]+$/, "");
      const trailingChars = url.slice(cleanUrl.length);
      return `[${cleanUrl}](${cleanUrl})${trailingChars}`;
    });
    
    // Återställ markdown-länkar
    placeholders.forEach((placeholder, index) => {
      protectedText = protectedText.replace(`__MARKDOWN_LINK_${index}__`, placeholder);
    });
    
    return protectedText;
  };

  return (
    <>
      {/* Floating toggle button - hidden when chat is open */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="fixed z-50 flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 shadow-xl hover:bg-primary/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary min-w-[44px] min-h-[44px] justify-center right-4 bottom-4 sm:right-5 sm:bottom-5 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:pb-2"
          aria-label="Fråga vår bilrådgivare"
          aria-expanded={isOpen}
          aria-controls="expert-chat-panel"
        >
          <MessageCircle className="w-5 h-5 shrink-0" aria-hidden />
          <span className="hidden sm:inline text-sm font-medium">
            Fråga vår bilrådgivare
          </span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          id="expert-chat-panel"
          className="fixed z-50 flex flex-col overflow-hidden bg-background shadow-2xl border border-border inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-full sm:max-w-sm sm:rounded-2xl sm:max-h-[600px] min-h-[100dvh] sm:min-h-0 sm:h-[min(calc(100vh-80px),600px)] pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)] pb-[env(safe-area-inset-bottom)]"
        >
          {/* Pre-chat consent – best practice för AI/ansvarsfriskrivning */}
          {!hasConsentedToChat ? (
            <div className="flex flex-col flex-1 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold">Bilspektrum Assistent</p>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                  aria-label="Stäng"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 flex flex-col justify-center text-center text-muted-foreground">
                <p className="text-lg mb-2">👋 Hej!</p>
                <p className="text-sm mb-6 max-w-sm mx-auto">
                  Det här är en AI-assistent som underlättar för dig. Bilspektrum tar inte ansvar för allt assistenten säger. För rätt och bäst hjälp,{" "}
                  <Link href={contactHref} className="text-primary font-medium underline hover:no-underline" onClick={() => setIsOpen(false)}>
                    kontakta Bilspektrum
                  </Link>
                  .
                </p>
                <p className="text-sm mb-6">Vill du använda chatten?</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setHasConsentedToChat(true);
                      setMessages((prev) =>
                        prev.length === 0 ? [WELCOME_MESSAGE] : prev
                      );
                    }}
                    className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                  >
                    Ja, börja chatta
                  </button>
                  <Link
                    href={contactHref}
                    onClick={() => setIsOpen(false)}
                    className="px-5 py-2.5 rounded-lg border border-border font-medium hover:bg-muted transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Kontakta oss
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/60 shrink-0">
            <div className="min-w-0 pr-2">
              <p className="text-sm font-semibold">Bilspektrum Assistent</p>
              <p className="text-xs text-muted-foreground">
                AI som underlättar. För officiell hjälp:{" "}
                <Link href={contactHref} className="text-primary font-medium underline hover:no-underline" onClick={() => setIsOpen(false)}>
                  kontakta Bilspektrum
                </Link>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {messages.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={downloadChat}
                    className="p-1.5 rounded-full hover:bg-muted transition-colors"
                    title="Ladda ner chatten"
                    aria-label="Ladda ner chatten"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={emailChat}
                    className="p-1.5 rounded-full hover:bg-muted transition-colors"
                    title="Skicka chatten via email"
                    aria-label="Skicka chatten via email"
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={startNewChat}
                    className="text-xs bg-primary/20 hover:bg-primary/30 text-primary px-3 py-1 rounded-full transition-colors"
                    title="Starta ny chatt"
                  >
                    Ny chatt
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-full hover:bg-muted transition-colors ml-auto"
                aria-label="Stäng chatten"
                title="Stäng chatten"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-2 px-3 py-3 overflow-y-auto text-sm">
            {messages.length === 0 && (
              <div className="text-center text-muted-foreground mt-6 px-2">
                <p className="text-lg mb-2">👋 Hej!</p>
                <p className="text-sm">
                  Fråga mig om våra bilar, Mazda, Hyundai eller Bilspektrums tjänster. För officiell hjälp kan du alltid{" "}
                  <Link href={contactHref} className="text-primary font-medium underline hover:no-underline" onClick={() => setIsOpen(false)}>
                    kontakta Bilspektrum
                  </Link>
                  .
                </p>
              </div>
            )}

            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`rounded-2xl px-3 py-2 max-w-[80%] ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap"
                      : "bg-muted text-foreground rounded-bl-sm"
                  }`}
                >
                  {msg.role === "assistant" ? (
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                        li: ({ children }) => <li className="ml-2">{children}</li>,
                        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                        em: ({ children }) => <em className="italic">{children}</em>,
                        code: ({ children, className }) => {
                          const isInline = !className;
                          return isInline ? (
                            <code className="bg-muted-foreground/20 px-1 py-0.5 rounded text-xs">{children}</code>
                          ) : (
                            <code className={className}>{children}</code>
                          );
                        },
                        h1: ({ children }) => <h1 className="text-base font-bold mt-2 mb-1">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-sm font-bold mt-2 mb-1">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
                        a: ({ href, children }) => {
                          let internalPath: string | null = href?.startsWith("/") ? href : null;
                          if (!internalPath && href) {
                            try {
                              const u = new URL(href, "https://example.com");
                              if (
                                u.pathname.startsWith("/fordon-i-lager") ||
                                u.pathname.startsWith("/kampanjer") ||
                                u.pathname === "/mazda" ||
                                u.pathname === "/hyundai"
                              ) {
                                internalPath = u.pathname;
                              }
                            } catch {
                              // not a valid URL, treat as external
                            }
                          }
                          if (internalPath) {
                            return (
                              <Link
                                href={internalPath}
                                className="text-primary underline hover:text-primary/80 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {children}
                              </Link>
                            );
                          }
                          return (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary underline hover:text-primary/80 transition-colors"
                            >
                              {children}
                            </a>
                          );
                        },
                      }}
                    >
                      {convertUrlsToLinks(msg.content)}
                    </ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm bg-muted px-3 py-2">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" />
                    <div
                      className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    />
                    <div
                      className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Kontaktformulär */}
          {showContactForm && (
            <div className="border-t border-border px-4 py-3 bg-muted/30">
              <div className="mb-3">
                <label className="block text-xs font-medium text-foreground mb-1">
                  Din e-postadress *
                </label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="namn@example.com"
                  className="w-full px-3 py-2 text-sm border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isSubmittingContact}
                />
              </div>
              <div className="mb-3">
                <label className="block text-xs font-medium text-foreground mb-1">
                  Ytterligare meddelande (valfritt)
                </label>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Berätta gärna mer om vad du är intresserad av..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  disabled={isSubmittingContact}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleContactRequest}
                  disabled={isSubmittingContact || !contactEmail}
                  className="flex-1 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmittingContact ? "Skickar..." : "Skicka förfrågan"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowContactForm(false);
                    setContactEmail("");
                    setContactMessage("");
                  }}
                  disabled={isSubmittingContact}
                  className="px-4 py-2 text-sm font-medium border border-input rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                >
                  Avbryt
                </button>
              </div>
            </div>
          )}

          {/* Knapp för att visa kontaktformulär */}
          {!showContactForm && messages.length > 0 && (
            <div className="border-t border-border px-4 py-3 bg-muted/30">
              <button
                type="button"
                onClick={() => setShowContactForm(true)}
                className="w-full px-4 py-2 text-sm font-medium bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Vill du bli kontaktad? Ange din e-post
              </button>
            </div>
          )}

          <p className="sr-only">
            AI-assistent. För officiell hjälp, kontakta Bilspektrum. Länk till kontakt i sidfoten.
          </p>
          <form onSubmit={handleSubmit} data-chat-form className="border-t border-border px-3 py-2 bg-background pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={1}
                className="flex-1 resize-none rounded-xl border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="Skriv ett meddelande…"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground p-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                aria-label="Skicka meddelande"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
            </>
          )}
        </div>
      )}
    </>
  );
}



